/**
 * Page component for the slug-based wizard route: /wizard/s/:slug
 *
 * Provisions (find-or-create) an instance for the current user,
 * then renders the wizard using the resulting instance ID.
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { provisionWizardBySlug } from "../services/naraDoor";
import { ServerDrivenWizardProvider } from "../components/wizard/ServerDrivenWizardProvider";
import { OnboardingWizard } from "../components/onboarding/OnboardingWizard";
import { AlertCircle, Loader2 } from "lucide-react";

export function WizardBySlugPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [instanceId, setInstanceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    provisionWizardBySlug(slug)
      .then((result) => {
        setInstanceId(result.instance_id);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [slug]);

  if (!slug) {
    return (
      <div className="min-h-[100dvh] bg-muted flex flex-col items-center justify-center gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-destructive text-lg font-medium">No wizard specified</p>
        <button
          onClick={() => navigate("/")}
          className="px-4 py-2 text-sm font-medium text-nara-orange hover:underline"
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[100dvh] bg-muted flex flex-col items-center justify-center gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-destructive text-lg font-medium">Failed to load wizard</p>
        <p className="text-sm text-muted-foreground max-w-md text-center">{error}</p>
        <button
          onClick={() => navigate("/")}
          className="px-4 py-2 text-sm font-medium text-nara-orange hover:underline"
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  if (!instanceId) {
    return (
      <div className="min-h-[100dvh] bg-muted flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 text-nara-orange animate-spin" />
        <p className="text-muted-foreground text-sm">Starting wizard...</p>
      </div>
    );
  }

  return (
    <ServerDrivenWizardProvider instanceId={instanceId}>
      <OnboardingWizard />
    </ServerDrivenWizardProvider>
  );
}
